// A glazing (docs/claude/glazings.md): a door and the fixed glass beside it with
// no wall between, built as the PT10 spec says — an aluminium frame in a steam
// room, a wooden one in a sauna. This module is the spec: the base layouts and
// how they grow with the width, the height, the door's width and position, the
// threshold and a corner return; the product code; and every solid of the
// assembly (profiles, glass, hinges, handle, seals) in the glazing's own frame.
// Pure and DOM-free (test/glazing.test.mjs).
//
// The glazing's frame: x along it from the frame's outer LEFT edge as seen from
// outside, y up from the floor, z across it from the glass mid-plane, + outside
// (the side the door opens to). All mm.

import type {
  GlazingConfig,
  GlazingFinish,
  GlazingGlass,
  GlazingHandle,
  GlazingOptions,
  Vec2,
} from '../shared/types';
import {
  convexMerge,
  earClip,
} from '../shared/triangulate';
import {
  PROFILE_A,
  PROFILE_B,
  PROFILE_C,
  PROFILE_D,
  PROFILE_E,
  SEAL_CINGI,
  SEAL_G023,
  SEAL_MAG,
  SEAL_MAG_MAGNET,
  SEAL_SE10,
  THRESHOLD,
} from './glazingData';

export type GlazingMaterial = 'aluminium' | 'wood';
export type GlazingLayout = 'centro' | 'sinistra' | 'destra';
export type GlazingHinge = 'sx' | 'dx';
export type { GlazingConfig, GlazingFinish, GlazingGlass, GlazingHandle, GlazingOptions };

export type GlazingSpec = Required<GlazingOptions>;

export const GLAZING_DEFAULTS: GlazingSpec = { config: 4, finish: '02', glassThickness: 10, glass: 'clear', treatedInside: false, handle: 'Q', threshold: false };

export function glazingOptions(o: GlazingOptions | undefined): GlazingSpec {
  return { ...GLAZING_DEFAULTS, ...(o ?? {}) };
}

/** A glazing as the configurator takes it: the wall opening (`W` × `H`), the
 *  door's leaf width and where the door is, the corner return. */
export interface GlazingInput extends GlazingSpec {
  mat: GlazingMaterial;
  /** Wall opening width (mm): the run from wall to wall (to the outer corner of
   *  a corner glazing). */
  W: number;
  /** Wall opening height (mm). */
  H: number;
  /** The door leaf's width (mm). */
  Dw: number;
  layout: GlazingLayout;
  hinge: GlazingHinge;
  /** A centred door's shift (mm, + to the right seen from outside). */
  off: number;
  /** A corner return: on the left or right seen from outside, its opening depth. */
  corner?: { side: 'L' | 'R'; D: number } | null;
}

// ---- The spec's figures -------------------------------------------------------

export const FRAME_BAND = 79;
/** The frame overlaps the masonry by 22 mm per side and at the top, less 5 mm
 *  of air: the opening is the frame less this, per side (aluminium only). */
export const WALL_IN = 17;
const MIN_FIXED = 200;
const SPLIT_OVER = 1050;
const JOINT = 2;
const SE10_H = 9.1;
const DOOR_W0 = 915;
const H0 = 2100;
const THRESHOLD_H = 35;
const CINGI_GAP = { out: 11.01, in: 11.55 };
/** Glass centre → the frame body's outside face. */
export const Z_OUT: Record<GlazingMaterial, number> = { aluminium: 15.73, wood: 34.5 };
export const WIDTH_MAX = 4000;
export const HEIGHT_RANGE: [number, number] = [1800, 2300];
export const DOOR_RANGE: [number, number] = [500, 1000];

type Span = [number, number];

interface Base {
  W: number; hinge: 'offset' | 'wall'; doorLeft?: boolean;
  fixed: Span[]; fixedY: Span; door: Span; doorY: Span; axis: Span; handle: Span; hingeY: number[]; hingeH: number;
  capX: Span; uprof: Span[]; hingeSeal: Span; grow: Span[];
}

const BASES: Record<'centro' | 'dx' | 'sx', Base> = {
  centro: {
    W: 1669, hinge: 'offset', fixed: [[62, 357], [1301, 1607]], fixedY: [4, 2038], door: [377, 1292], doorY: [20, 2010],
    axis: [1296.5, 29.5], handle: [472, 502], hingeY: [339, 1720], hingeH: 194, capX: [357, 1301], uprof: [[79, 357], [1301, 1590]], hingeSeal: [1292, 1299], grow: [[357, 0.5], [1500, 0.5]],
  },
  dx: {
    W: 1369, hinge: 'wall', fixed: [[62, 346]], fixedY: [4, 2038], door: [366, 1281], doorY: [20, 2010],
    axis: [1272.5, 29.5], handle: [461, 491], hingeY: [340, 1721], hingeH: 204, capX: [346, 1290], uprof: [[79, 346]], hingeSeal: [1281, 1288], grow: [[346, 1]],
  },
  sx: {
    W: 1369, hinge: 'offset', doorLeft: true, fixed: [[1023, 1307]], fixedY: [4, 2038], door: [99, 1014], doorY: [20, 2010],
    axis: [1018.5, 29.5], handle: [194, 224], hingeY: [339, 1720], hingeH: 194, capX: [79, 1023], uprof: [[1023, 1290]], hingeSeal: [1014, 1021], grow: [[1100, 1]],
  },
};

const MAP: Record<GlazingLayout, Record<GlazingHinge, ['centro' | 'dx' | 'sx', boolean]>> = {
  centro: { dx: ['centro', false], sx: ['centro', true] },
  destra: { dx: ['dx', false], sx: ['sx', true] },
  sinistra: { dx: ['sx', false], sx: ['dx', true] },
};

/** A layout in the base frame (hinges on the right of the door; mirrored in x
 *  for hinges on the left), as the configurator holds it. */
export interface GlazingLayoutV extends Base {
  H: number; profile: 'A' | 'B'; soglia: boolean; wood: boolean; noStoppers?: boolean; capCut?: 'L' | 'R';
  /** A hinge's own plan outlines (flat [x from the axis, z]): the door's arm, and
   *  a wall hinge's bracket on the frame — else PT10's. */
  hingeArm?: readonly number[]; hingeBracket?: readonly number[];
}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

function sized(v0: GlazingLayoutV, W: number): GlazingLayoutV {
  const d = W - v0.W, sh = (x: number): number => x + v0.grow.reduce((s, [t, f]) => s + (x >= t ? f * d : 0), 0);
  const pair = (p: Span): Span => [sh(p[0]), sh(p[1])];
  return { ...v0, W, fixed: v0.fixed.map(pair), door: pair(v0.door), axis: [sh(v0.axis[0]), v0.axis[1]], handle: pair(v0.handle), capX: pair(v0.capX), uprof: v0.uprof.map(pair), hingeSeal: pair(v0.hingeSeal) };
}

function doorSized(v0: Base, Dw: number, H: number, key: 'centro' | 'dx' | 'sx', profile: 'A' | 'B'): GlazingLayoutV {
  const c = { ...clone(v0), H, profile, soglia: false, wood: false } as GlazingLayoutV, dd = Dw - DOOR_W0;
  const [dl, dr] = key === 'centro' ? [dd / 2, dd / 2] : key === 'dx' ? [dd, 0] : [0, dd];
  const fLi = c.doorLeft ? -1 : 0, fRi = c.doorLeft ? 0 : (c.fixed.length > 1 ? 1 : -1);
  const fLend = fLi >= 0 ? c.fixed[fLi][1] : null, fRstart = fRi >= 0 ? c.fixed[fRi][0] : null;
  c.door[0] -= dl; c.handle = [c.handle[0] - dl, c.handle[1] - dl];
  c.door[1] += dr; c.axis[0] += dr; c.hingeSeal = [c.hingeSeal[0] + dr, c.hingeSeal[1] + dr];
  if (fLi >= 0) { c.fixed[fLi][1] -= dl; c.capX[0] -= dl; c.uprof.forEach((u) => { if (u[1] === fLend) u[1] -= dl; }); }
  if (fRi >= 0) { c.fixed[fRi][0] += dr; c.capX[1] += dr; c.uprof.forEach((u) => { if (u[0] === fRstart) u[0] += dr; }); }
  c.grow = key === 'centro' ? [[c.fixed[0][1], 0.5], [(c.fixed[1][0] + c.fixed[1][1]) / 2, 0.5]]
    : key === 'dx' ? [[c.fixed[0][1], 1]] : [[(c.fixed[0][0] + c.fixed[0][1]) / 2, 1]];
  const dH = H - H0;
  c.fixedY = [c.fixedY[0], c.fixedY[1] + dH]; c.doorY = [c.doorY[0], c.doorY[1] + dH]; c.hingeY = [c.hingeY[0], c.hingeY[1] + dH];
  return c;
}

function offsetRange(v: GlazingLayoutV, minFixed = MIN_FIXED): Span {
  if (v.fixed.length < 2 || v.doorLeft) return [0, 0];
  return [-Math.floor(v.fixed[0][1] - v.fixed[0][0] - minFixed), Math.floor(v.fixed[1][1] - v.fixed[1][0] - minFixed)];
}

function shiftDoor(v: GlazingLayoutV, s: number): void {
  if (!s) return;
  const e0 = v.fixed[0][1], e1 = v.fixed[1][0], m = (x: number): number => x + s;
  v.door = [m(v.door[0]), m(v.door[1])]; v.handle = [m(v.handle[0]), m(v.handle[1])]; v.axis = [m(v.axis[0]), v.axis[1]]; v.capX = [m(v.capX[0]), m(v.capX[1])];
  v.hingeSeal = [m(v.hingeSeal[0]), m(v.hingeSeal[1])];
  v.fixed = [[v.fixed[0][0], m(e0)], [m(e1), v.fixed[1][1]]];
  v.uprof = v.uprof.map(([a, b]) => [a === e1 ? m(a) : a, b === e0 ? m(b) : b]);
}

function widthRange(v0: GlazingLayoutV, minFixed = MIN_FIXED): Span {
  const minW = Math.max(...v0.fixed.map((f) => v0.W - ((f[1] - f[0]) - minFixed) / (v0.fixed.length > 1 ? 0.5 : 1)));
  return [Math.ceil(minW / 5) * 5, WIDTH_MAX];
}

/** No fixed pane wider than 1050 mm: as many equal panes as needed, 2 mm joints. */
export function panesOf(f: Span): Span[] {
  const L = f[1] - f[0], n = Math.max(1, Math.ceil((L + JOINT) / (SPLIT_OVER + JOINT))), w = (L - JOINT * (n - 1)) / n;
  return Array.from({ length: n }, (_, i) => { const a = f[0] + i * (w + JOINT); return [a, a + w] as Span; });
}

/** The left / right fixed run of a layout (base frame), or null. */
const fixedL = (v: GlazingLayoutV): Span | null => (v.doorLeft ? null : v.fixed[0]);
const fixedR = (v: GlazingLayoutV): Span | null => (v.doorLeft ? v.fixed[0] : v.fixed[1] ?? null);

/** How far each side of the opening the frame reaches past it (mm). */
export function holeIn(mat: GlazingMaterial): number { return mat === 'wood' ? 0 : WALL_IN; }

/** The corner glazing's checks: on which base side it is, whether the door
 *  stands against it, and whether that is the hinge side (refused). */
interface CornerCheck { sg: 1 | -1; E: 'L' | 'R'; adj: boolean; bad: boolean }

function cornerCheck(v: GlazingLayoutV, mirror: boolean, side: 'L' | 'R'): CornerCheck {
  const sg = side === 'R' ? 1 : -1, E = sg * (mirror ? -1 : 1) > 0 ? 'R' : 'L';
  const adj = E === 'R' ? v.fixed.every((f) => f[1] < v.door[1]) : v.fixed.every((f) => f[0] > v.door[0]);
  const hingeR = Math.abs(v.axis[0] - v.door[1]) < Math.abs(v.axis[0] - v.door[0]);
  return { sg, E, adj, bad: adj && (E === 'R' ? hingeR : !hingeR) };
}

/** A glazing worked out: the front run's layout (base frame), the mirror, the
 *  corner it turns (null when none, or refused) and what is out of the spec. */
export interface GlazingPlan {
  input: GlazingInput;
  v: GlazingLayoutV;
  mirror: boolean;
  corner: (CornerCheck & { D: number }) | null;
  /** The frame's overall width and height (mm). */
  frameW: number;
  frameH: number;
  warnings: GlazingWarning[];
}

export type GlazingWarning =
  | { kind: 'cornerDepth'; min: number }
  | { kind: 'cornerHinge' }
  | { kind: 'door'; min: number; max: number }
  | { kind: 'height'; min: number; max: number }
  | { kind: 'heightMax'; max: number }
  | { kind: 'offset'; min: number; max: number }
  | { kind: 'width'; min: number; max: number }
  | { kind: 'widthMax'; max: number };

/** The configurator's `render()` up to the 3D build: the layout for an input. */
export function planGlazing(input: GlazingInput): GlazingPlan {
  const wood = input.mat === 'wood';
  const warnings: GlazingWarning[] = [];
  const [key, mirror] = MAP[input.layout][input.hinge];
  const profile: 'A' | 'B' = !wood && input.config === 4 ? 'B' : 'A';
  // A corner on the side the door's hinges are, against the door, is refused.
  let cornerOn = false;
  const holeW = (corner: boolean): number => (corner ? 1 : 2) * holeIn(input.mat);
  const frameH = input.H + holeIn(input.mat);
  const geoDw = Math.max(200, input.Dw);
  const base = doorSized(BASES[key], geoDw, frameH, key, profile);
  const plan = (corner: boolean): { v: GlazingLayoutV; wR: Span } => {
    const wR = widthRange(base);
    // Never fewer than 60 mm of fixed glass: past the spec's limits the glazing
    // still builds, as near as it can.
    const geoMin = widthRange(base, 60)[0];
    const W = Math.max(geoMin, input.W + holeW(corner));
    const v = sized(base, W);
    if (input.threshold) { v.soglia = true; v.fixedY = [18, v.fixedY[1]]; v.doorY = [47, v.doorY[1]]; v.uprof = []; }
    if (input.layout === 'centro') {
      const [a, b] = offsetRange(v), [lo, hi] = mirror ? [-b, -a] : [a, b];
      const [ga, gb] = offsetRange(v, 60), [glo, ghi] = mirror ? [-gb, -ga] : [ga, gb];
      const off = Math.round(input.off || 0);
      if (!corner && (off < lo || off > hi)) warnings.push({ kind: 'offset', min: lo, max: hi });
      const o = Math.min(ghi, Math.max(glo, off));
      shiftDoor(v, mirror ? -o : o);
    }
    if (wood) woodLayout(v);
    return { v, wR };
  };
  let { v, wR } = plan(false);
  let cc: CornerCheck | null = null;
  if (input.corner && input.corner.D > 0) {
    cc = cornerCheck(v, mirror, input.corner.side);
    if (cc.bad) warnings.push({ kind: 'cornerHinge' });
    else {
      cornerOn = true;
      ({ v, wR } = plan(true));
      cc = cornerCheck(v, mirror, input.corner.side);
      const min = cornerMinD(input);
      if (input.corner.D < min) warnings.push({ kind: 'cornerDepth', min });
    }
  }
  const hw = holeW(cornerOn);
  if (input.W + hw < wR[0] || input.W + hw > wR[1]) warnings.push({ kind: 'width', min: wR[0] - hw, max: wR[1] - hw });
  if (input.H < HEIGHT_RANGE[0] || input.H > HEIGHT_RANGE[1]) warnings.push({ kind: 'height', min: HEIGHT_RANGE[0], max: HEIGHT_RANGE[1] });
  if (input.Dw < DOOR_RANGE[0] || input.Dw > DOOR_RANGE[1]) warnings.push({ kind: 'door', min: DOOR_RANGE[0], max: DOOR_RANGE[1] });
  const corner = cornerOn && cc && input.corner ? { ...cc, D: input.corner.D } : null;
  return { input, v, mirror, corner, frameW: v.W, frameH, warnings };
}

/** The smallest corner return the spec allows (its opening depth, mm). */
export function cornerMinD(input: Pick<GlazingInput, 'mat' | 'glassThickness'>): number {
  const wood = input.mat === 'wood', pocket = wood ? 61 : 62;
  return Math.ceil(MIN_FIXED + pocket + Z_OUT[input.mat] + input.glassThickness / 2 + 2 - (wood ? 0 : WALL_IN));
}

/** Wood: every glass gap 2 mm (no seals), panes 18 mm into the wooden slots. */
function woodLayout(v: GlazingLayoutV): void {
  v.wood = true; v.profile = 'A';
  const fL = fixedL(v), fR = fixedR(v);
  const hingeRight = Math.abs(v.axis[0] - v.door[1]) < Math.abs(v.axis[0] - v.door[0]);
  let d = 0;
  if (!fR) d = (v.W - 79 - 2) - v.door[1]; else if (!fL) d = (79 + 2) - v.door[0];
  v.door = [v.door[0] + d, v.door[1] + d]; v.handle = [v.handle[0] + d, v.handle[1] + d]; v.axis = [v.axis[0] + d, v.axis[1]];
  if (fL) { fL[0] = 61; fL[1] = v.door[0] - 2; }
  if (fR) { fR[0] = v.door[1] + 2; fR[1] = v.W - 61; }
  if (v.hinge === 'offset') v.axis[0] = hingeRight ? v.door[1] + 1 : v.door[0] - 1;
  v.fixedY = [v.soglia ? 16 : 4, v.H - 60]; v.doorY = [v.soglia ? 38 : 20, v.H - 81];
  v.uprof = v.soglia ? [] : [fL, fR].filter((f): f is Span => !!f).map((f) => [Math.max(f[0], 79), Math.min(f[1], v.W - 79)]);
}

// ---- The product code ---------------------------------------------------------

const PC_ALIGN: Record<GlazingLayout, string> = { centro: 'A', sinistra: 'B', destra: 'C' };
const PC_GLASS: Record<GlazingGlass, string> = { clear: 'T', low: 'X', acid: 'A', bronze: 'B', grey: 'G', refl: 'R' };

/** Whether a glass type has a treated face (and so the inside / outside choice). */
export const hasTreatedFace = (g: GlazingGlass): boolean => g === 'acid' || g === 'refl';

/** The configurator's product code:
 *  PT10.W.H.Door.Align.Hinges.HingeType.Config.Glass.Finish.GlassColour.Handle[.S][.C…][.L][.D…] */
export function productCode(p: GlazingPlan): string {
  const i = p.input;
  return [
    'PT10', Math.round(i.W), Math.round(i.H), Math.round(i.Dw), PC_ALIGN[i.layout], i.hinge.toUpperCase(), i.glassThickness === 10 ? 'A' : 'F',
    String(i.config), String(i.glassThickness).padStart(2, '0'), i.finish, PC_GLASS[i.glass] + (hasTreatedFace(i.glass) && i.treatedInside ? 'I' : ''), i.handle,
    ...(i.threshold ? ['S'] : []), ...(p.corner ? [`C${i.corner!.side}${Math.round(p.corner.D)}`] : []), ...(i.mat === 'wood' ? ['L'] : []),
    ...(i.layout === 'centro' && Math.round(i.off) ? [`D${i.off > 0 ? '+' : ''}${Math.round(i.off)}`] : []),
  ].join('.');
}

/** The PT10 configurator opened on a glazing (its cut lists live there). */
export function configuratorUrl(code: string, lang: string): string {
  return `https://pt10.stenal.com/?p=${encodeURIComponent(code)}&l=${encodeURIComponent(lang)}`;
}

// ---- Solids -------------------------------------------------------------------

export interface P3 { x: number; y: number; z: number }

/** What a solid is made of, for its colour and its weight. */
export type GlazingStuff =
  | 'gasket'
  | 'glass'
  | 'hardware'
  | 'magnet'
  | 'profile'
  | 'seal'
  | 'silicone'
  | 'wood';

/** Which part of the glazing a solid belongs to. `fixed` carries the pane's
 *  index along the front run (left to right as seen from outside), `side` the
 *  corner return's. */
export type GlazingPart =
  | { kind: 'door' }
  | { kind: 'fixed'; index: number }
  | { kind: 'frame' }
  | { kind: 'side'; index: number };

/** One solid: a section (its outline, then its holes) swept straight from one
 *  end to the other, each point of the section to its own place at each end — a
 *  mitred end is a plane, so this is still a prism. `hinge` marks the hinges
 *  (2 kg each, as the configurator weighs them). */
export interface GlazingSolid {
  stuff: GlazingStuff;
  part: GlazingPart;
  leaf?: true;
  hinge?: true;
  sec: Vec2[][];
  ends: [P3[][], P3[][]];
}

const area2 = (l: readonly Vec2[]): number => l.reduce((t, q, k) => { const r = l[(k + 1) % l.length]; return t + q.x * r.y - r.x * q.y; }, 0);
/** The outline counter-clockwise, the holes clockwise (a closed mesh needs the
 *  holes' sides facing the other way). */
const orient = (loops: Vec2[][]): Vec2[][] => loops.map((l, k) => ((area2(l) > 0) === (k === 0) ? l : l.slice().reverse()));

const ring = (flat: readonly number[]): Vec2[] => {
  const out: Vec2[] = [];
  for (let i = 0; i + 1 < flat.length; i += 2) out.push({ x: flat[i], y: flat[i + 1] });
  return out;
};
const loopsOf = (flats: readonly (readonly number[])[]): Vec2[][] => flats.map(ring);
const pairs = (pts: ReadonlyArray<readonly [number, number]>): Vec2[] => pts.map(([x, y]) => ({ x, y }));

/** A circle as a ring (radius, centre, segments). */
const circle = (cx: number, cy: number, r: number, n = 20): Vec2[] => Array.from({ length: n }, (_, k) => ({ x: cx + r * Math.cos((2 * Math.PI * k) / n), y: cy + r * Math.sin((2 * Math.PI * k) / n) }));

const rrect = (x0: number, x1: number, z0: number, z1: number, r: number, seg = 4): Vec2[] => {
  const pts: Vec2[] = [];
  for (const [cx, cz, a0] of [[x1 - r, z1 - r, 0], [x0 + r, z1 - r, 90], [x0 + r, z0 + r, 180], [x1 - r, z0 + r, 270]]) {
    for (let k = 0; k <= seg; k++) { const a = ((a0 + (90 * k) / seg) * Math.PI) / 180; pts.push({ x: cx + r * Math.cos(a), y: cz + r * Math.sin(a) }); }
  }
  return pts;
};

const rect = (x0: number, y0: number, x1: number, y1: number): Vec2[] => [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];

// The hinges (export/PT10-geometria.json `hinges`): plan polygons, x from the
// axis (− toward the door), z from the glass mid-plane.
const HINGE = {
  offset: { r: 13.75, H: 194, doorPlate: rrect(-45.5, -4.5, -13, -5.2, 2), doorArm: pairs([[-45.5, 5.2], [-9, 5.2], [-3, 14], [-6, 29], [-14, 24], [-24, 13], [-45.5, 10.5]]), fixedPlate: rrect(4.5, 45.5, -13, -5.2, 2), fixedArm: pairs([[45.5, 5.2], [9, 5.2], [3, 14], [6, 29], [14, 24], [24, 13], [45.5, 10.5]]) },
  wall: { r: 14, H: 204 },
};

/** The wooden frame's sections (uploads/Jambs.dxf): [x from the frame edge, z]. */
const woodJamb = (slot: number, trim: boolean): Vec2[] => pairs([[0, 34.5], [0, -42.5], [9.5, -42.5], [9.5, -32.5], [50.5, -32.5], [50.5, -42.5], [79, -42.5], [79, -slot], [60, -slot], [60, slot], [79, slot], ...(trim ? [[79, 64.5], [49, 64.5], [49, 34.5]] as Array<[number, number]> : [[79, 34.5]] as Array<[number, number]>)]);
/** The wooden head: [distance from the top edge, z], slotted over fixed glass. */
const woodHead = (slot: number, slotted: boolean): Vec2[] => pairs([[79, 34.5], ...(slotted ? [[79, slot], [60, slot], [60, -slot], [79, -slot]] as Array<[number, number]> : []), [79, -42.5], [65, -42.5], [65, -32.5], [23, -32.5], [23, -7.5], [0, -7.5], [0, 34.5]]);
/** The wooden threshold: [z, height], slotted under fixed glass. */
const woodSill = (slot: number, slotted: boolean): Vec2[] => pairs([[-42.5, 0], [-42.5, 35], ...(slotted ? [[-slot, 35], [-slot, 16], [slot, 16], [slot, 35]] as Array<[number, number]> : []), [34.5, 35], [34.5, 5], [29.5, 5], [29.5, 0]]);

/** Everything the glazing is made of, in its own frame (see the top), with the
 *  door turned `open` degrees outward on its hinges. */
export function glazingSolids(p: GlazingPlan, open = 0): GlazingSolid[] {
  const i = p.input;
  if (!p.corner) return finish(buildRun(p.v, i, { open }), p.v.W, p.mirror);
  return cornerSolids(p, open);
}

interface RunOpts { open: number; dropJamb?: 'L' | 'R'; noDoor?: boolean; bare?: boolean }

/** The door leaf alone, in the base frame (hinges on the right): glass, handle and
 *  hinges, no frame, seals or strike (the single door's own frame carries those). */
export function leafSolids(v: GlazingLayoutV, i: GlazingInput, open = 0): GlazingSolid[] {
  return buildRun(v, i, { open, bare: true });
}

export { loopsOf, mirrorSolid, orient };

/** Mirror into the frame as seen from outside, and number the fixed panes left
 *  to right. */
function finish(solids: GlazingSolid[], W: number, mirror: boolean): GlazingSolid[] {
  const out = mirror ? solids.map((s) => mirrorSolid(s, W)) : solids;
  numberPanes(out);
  return out;
}

function mirrorSolid(s: GlazingSolid, W: number): GlazingSolid {
  const m = (q: P3): P3 => ({ x: W - q.x, y: q.y, z: q.z });
  return { ...s, sec: s.sec.map((l) => l.map((q) => ({ x: -q.x, y: q.y }))), ends: [s.ends[0].map((l) => l.map(m)), s.ends[1].map((l) => l.map(m))] };
}

function numberPanes(solids: GlazingSolid[]): void {
  const panes = solids.filter((s) => s.stuff === 'glass' && s.part.kind === 'fixed');
  const xs = panes.map((s) => centreX(s));
  const order = [...new Set(xs.map((x) => Math.round(x)))].sort((a, b) => a - b);
  panes.forEach((s, k) => { s.part = { kind: 'fixed', index: order.indexOf(Math.round(xs[k])) }; });
}

const centreX = (s: GlazingSolid): number => {
  let lo = Infinity, hi = -Infinity;
  for (const e of s.ends) for (const l of e) for (const q of l) { lo = Math.min(lo, q.x); hi = Math.max(hi, q.x); }
  return (lo + hi) / 2;
};

/** One run of a glazing (the configurator's `build`): its solids in the base frame. */
function buildRun(v: GlazingLayoutV, i: GlazingInput, o: RunOpts): GlazingSolid[] {
  const out: GlazingSolid[] = [];
  const W = v.W, H = v.H, GT = i.glassThickness / 2;
  const frame: GlazingPart = { kind: 'frame' }, doorPart: GlazingPart = { kind: 'door' };
  const put = (stuff: GlazingStuff, part: GlazingPart, sec: Vec2[][], at: (q: Vec2, end: 0 | 1) => P3, extra: Partial<GlazingSolid> = {}): void => {
    const s = orient(sec.filter((l) => l.length >= 3));
    if (!s.length) return;
    out.push({ stuff, part, sec: s, ends: [s.map((l) => l.map((q) => at(q, 0))), s.map((l) => l.map((q) => at(q, 1)))], ...extra });
  };
  // A vertical member: section [x from the left outer edge, z], from bot(x) to top(x).
  const vert = (stuff: GlazingStuff, sec: Vec2[][], side: 'L' | 'R', top: (x: number) => number, bot: (x: number) => number = () => 0, part = frame): void => {
    if (o.dropJamb === side) return;
    const X = (x: number): number => (side === 'L' ? x : W - x);
    put(stuff, part, sec, (q, e) => ({ x: X(q.x), y: e ? top(q.x) : bot(q.x), z: q.y }));
  };
  const mitreTop = (x: number): number => H - x;
  // A head member: section [distance from the top, z], from x0(s) to x1(s).
  const head = (stuff: GlazingStuff, sec: Vec2[][], x0: (s: number) => number, x1: (s: number) => number): void =>
    put(stuff, frame, sec, (q, e) => ({ x: e ? x1(q.x) : x0(q.x), y: H - q.x, z: q.y }));
  // A member along x with a section [z, y], from x0 to x1 (each may follow the point).
  const along = (stuff: GlazingStuff, part: GlazingPart, sec: Vec2[][], x0: (q: Vec2) => number, x1: (q: Vec2) => number, extra?: Partial<GlazingSolid>): void =>
    put(stuff, part, sec, (q, e) => ({ x: e ? x1(q) : x0(q), y: q.y, z: q.x }), extra);
  // A plan extrusion: section [x, z] from y0 to y1.
  const plan = (stuff: GlazingStuff, part: GlazingPart, sec: Vec2[][], y0: number, y1: number, extra?: Partial<GlazingSolid>): void =>
    put(stuff, part, sec, (q, e) => ({ x: q.x, y: e ? y1 : y0, z: q.y }), extra);
  const box = (stuff: GlazingStuff, part: GlazingPart, x0: number, x1: number, y0: number, y1: number, z0: number, z1: number, extra?: Partial<GlazingSolid>): void => {
    if (Math.abs(x1 - x0) < 1e-6 || Math.abs(y1 - y0) < 1e-6 || Math.abs(z1 - z0) < 1e-6) return;
    plan(stuff, part, [rect(Math.min(x0, x1), Math.min(z0, z1), Math.max(x0, x1), Math.max(z0, z1))], Math.min(y0, y1), Math.max(y0, y1), extra);
  };
  // Threshold sections: a jamb section [x, z] lies in the threshold at height x − 44.
  const td = (l: Vec2[]): Vec2[] => l.map((q) => ({ x: q.y, y: q.x - 44 }));
  const fL = fixedL(v), fR = fixedR(v);
  const UH = GT < 4.5 ? 12 : 15;
  const uSec = (): Vec2[][] => {
    const e = loopsOf(PROFILE_E);
    if (UH === 15) return e;
    const k = 0.8, zc = 0.11;
    return e.map((l) => l.map((q) => ({ x: zc + (q.x - zc) * k, y: q.y * k })));
  };

  if (o.bare) {
    // the single door's frame is its own module's
  } else if (v.wood) {
    const slot = GT < 4.5 ? 4.5 : 5.5, trim = i.config === 4;
    const jb = v.soglia ? (): number => THRESHOLD_H : (): number => 0;
    vert('wood', [woodJamb(slot, trim)], 'L', () => H - 79, jb);
    vert('wood', [woodJamb(slot, trim)], 'R', () => H - 79, jb);
    // Head and threshold: one piece each, the glass slot only over / under the fixed panes.
    const slots: Span[] = [];
    if (fL) slots.push([60, (fL[1] + v.door[0]) / 2]);
    if (fR) slots.push([(v.door[1] + fR[0]) / 2, W - 60]);
    const cuts = [...new Set([0, W, ...slots.flat()].map((x) => Math.round(x * 1000) / 1000))].filter((x) => x >= 0 && x <= W).sort((a, b) => a - b);
    for (let k = 0; k + 1 < cuts.length; k++) {
      const a = cuts[k], b = cuts[k + 1], m = (a + b) / 2, slotted = slots.some(([s, e]) => m > s && m < e);
      head('wood', [woodHead(slot, slotted)], () => a, () => b);
      if (v.soglia) along('wood', frame, [woodSill(slot, slotted)], () => a, () => b);
    }
    // The trim over the head (and the threshold), in line with the jambs': a
    // member along x, so a corner's mitre cuts it as it cuts the head.
    if (trim) {
      head('wood', [rect(49, 34.5, 79, 64.5)], () => 49, () => W - 49);
      if (v.soglia) along('wood', frame, [rect(34.5, 5, 64.5, 35)], () => 49, () => W - 49);
    }
    for (const [a, b] of v.uprof) along('profile', frame, uSec(), () => a, () => b);
  } else {
    const A = loopsOf(PROFILE_A), B = loopsOf(PROFILE_B), C = loopsOf(PROFILE_C), D = loopsOf(PROFILE_D);
    vert('profile', A, 'L', mitreTop);
    vert('profile', A, 'R', mitreTop);
    head('profile', A, (s) => s, (s) => W - s);
    if (v.profile === 'B') {
      const tb = v.soglia ? (x: number): number => x - 44 : (): number => 0;
      vert('profile', B, 'L', mitreTop, tb);
      vert('profile', B, 'R', mitreTop, tb);
      head('profile', B, (s) => s, (s) => W - s);
    }
    if (fL) { vert('profile', C, 'L', mitreTop); head('profile', C, (s) => s, () => fL[1]); }
    if (fR) { vert('profile', C, 'R', mitreTop); head('profile', C, () => fR[0], (s) => W - s); }
    // The flat filler in the empty glass pocket: over the door, and down a jamb
    // with no fixed glass beside it.
    head('profile', D, fL ? () => v.capX[0] : (s) => s, fR ? () => v.capX[1] : (s) => W - s);
    if (!fR) vert('profile', D, 'R', mitreTop);
    if (!fL) vert('profile', D, 'L', mitreTop);
    if (v.soglia) {
      along('profile', frame, [ring(THRESHOLD[0]).map((q) => ({ x: q.x - 29.27, y: q.y }))], () => 79, () => W - 79);
      if (fL) along('profile', frame, C.map(td), () => 79, () => fL[1]);
      if (fR) along('profile', frame, C.map(td), () => fR[0], () => W - 79);
      along('profile', frame, D.map(td), () => (fL || v.capCut === 'L' ? v.capX[0] : 79), () => (fR || v.capCut === 'R' ? v.capX[1] : W - 79));
      if (v.profile === 'B') {
        along('profile', frame, B.map(td), (q) => 44 + q.y, (q) => W - 44 - q.y);
        box('profile', frame, 49, W - 49, 0, 5, 15.73, 35.73);
      }
    }
    for (const [a, b] of v.uprof) along('profile', frame, uSec(), () => a, () => b);
  }

  // The fixed glass, split at 1050 mm, with a 2 × 4 silicone strip in each joint.
  for (const f of [fL, fR]) {
    if (!f) continue;
    const ps = panesOf(f);
    ps.forEach((q) => box('glass', { kind: 'fixed', index: 0 }, q[0], q[1], v.fixedY[0], v.fixedY[1], -GT, GT));
    if (!v.wood) for (let k = 1; k < ps.length; k++) box('silicone', frame, ps[k - 1][1], ps[k][0], v.fixedY[0], v.fixedY[1], -2, 2);
  }

  if (!v.wood && !o.bare) {
    // The magnetic strike on the fixed glass edge (or on the jamb), facing the door's.
    const fe = fL ? fL[1] : 79, y0 = v.soglia ? THRESHOLD_H : fL ? v.fixedY[0] : 0, y1 = H - 79;
    put('seal', frame, loopsOf(SEAL_MAG), (q, e) => ({ x: fe + q.x, y: e ? y1 : y0, z: -q.y }));
    put('magnet', frame, loopsOf(SEAL_MAG_MAGNET), (q, e) => ({ x: fe + q.x, y: e ? y1 : y0, z: -q.y }));
  }

  // The door leaf on the hinge axis (turned below).
  const leafStart = out.length;
  if (!o.noDoor) {
    const de = v.door[0], dy0 = v.doorY[0], dy1 = v.doorY[1];
    box('glass', doorPart, v.door[0], v.door[1], dy0, dy1, -GT, GT, { leaf: true });
    if (!v.wood && !o.bare) {
      put('seal', doorPart, loopsOf(SEAL_MAG), (q, e) => ({ x: de - q.x, y: e ? dy1 + SE10_H : dy0, z: q.y }));
      put('magnet', doorPart, loopsOf(SEAL_MAG_MAGNET), (q, e) => ({ x: de - q.x, y: e ? dy1 : dy0, z: q.y }));
      for (const [a, b] of hingeSealSegs(v)) put('seal', doorPart, loopsOf(SEAL_SE10), (q, e) => ({ x: v.door[1] + q.x, y: e ? b : a, z: q.y }));
      along('seal', doorPart, loopsOf(SEAL_G023).map((l) => l.map((q) => ({ x: -q.x, y: dy0 + q.y }))), () => v.door[0], () => v.door[1]);
      along('seal', doorPart, loopsOf(SEAL_SE10).map((l) => l.map((q) => ({ x: q.y, y: dy1 + q.x }))), () => v.door[0], () => v.door[1]);
    }
    const [ha, hb] = v.handle;
    for (const s of [1, -1]) {
      if (i.handle === 'R') {
        const hx = (ha + hb) / 2, yc = (v.doorY[0] + v.doorY[1]) / 2;
        plan('hardware', doorPart, [circle(hx, s * (GT + 75), 15)], yc - 250, yc + 250, { leaf: true });
        for (const dy of [-175, 175]) {
          put('hardware', doorPart, [circle(hx, yc + dy, 13.5)], (q, e) => ({ x: q.x, y: q.y, z: s * (e ? GT + 60 : GT) }), { leaf: true });
        }
      } else {
        box('hardware', doorPart, ha, hb, 120, 1100, s * 50, s * 80, { leaf: true });
        box('hardware', doorPart, ha, hb, 120, 150, s * GT, s * 50, { leaf: true });
        box('hardware', doorPart, ha, hb, 1070, 1100, s * GT, s * 50, { leaf: true });
      }
    }
  }
  // The hinges: split knuckles on an axis 29.5 mm outside the glass, the door's
  // half turning with the leaf.
  const hingeParts: Array<{ door: boolean; s: GlazingSolid }> = [];
  if (!o.noDoor) {
    const AX = v.axis[0], AZ = v.axis[1];
    const T = v.hinge === 'wall' ? HINGE.wall : HINGE.offset, r = T.r, H2 = v.hingeH / 2;
    const knuckle = (part: GlazingPart, ya: number, yb: number, domeUp: boolean, isDoor: boolean): void => {
      const n0 = out.length;
      plan('hardware', part, [circle(AX, AZ, r, 24)], ya, yb, { hinge: true });
      // The domed cap, as three steps.
      const yTop = domeUp ? yb : ya, sgn = domeUp ? 1 : -1, hgt = r * 0.22;
      [[0.95, 0.4], [0.8, 0.35], [0.5, 0.25]].reduce((at, [k, f]) => {
        const nx = at + sgn * hgt * f;
        plan('hardware', part, [circle(AX, AZ, r * k, 24)], Math.min(at, nx), Math.max(at, nx), { hinge: true });
        return nx;
      }, yTop);
      for (let k = n0; k < out.length; k++) hingeParts.push({ door: isDoor, s: out[k] });
    };
    const shift = (l: Vec2[]): Vec2[] => l.map((q) => ({ x: AX + q.x, y: q.y }));
    for (const yc of v.hingeY) {
      knuckle(doorPart, yc + 1, yc + H2, true, true);
      let n0 = out.length;
      plan('hardware', doorPart, [shift(HINGE.offset.doorPlate)], yc + 2, yc + 87, { hinge: true });
      plan('hardware', doorPart, [shift(v.hingeArm ? ring(v.hingeArm) : HINGE.offset.doorArm)], yc + 2, yc + 87, { hinge: true });
      for (let k = n0; k < out.length; k++) hingeParts.push({ door: true, s: out[k] });
      knuckle(frame, yc - H2, yc - 1, false, false);
      n0 = out.length;
      if (v.hinge === 'offset') {
        plan('hardware', frame, [shift(HINGE.offset.fixedPlate)], yc - 87, yc - 2, { hinge: true });
        plan('hardware', frame, [shift(HINGE.offset.fixedArm)], yc - 87, yc - 2, { hinge: true });
      } else if (v.hingeBracket) {
        plan('hardware', frame, [shift(ring(v.hingeBracket))], yc - H2 + 2, yc - 2, { hinge: true });
      } else {
        const zt = v.profile === 'B' ? 38 : 15;
        plan('hardware', frame, [shift(rrect(10.5, 17.5, -9, zt, 1.5))], yc - 97, yc - 2, { hinge: true });
        plan('hardware', frame, [shift(pairs([[6, 22], [12, 22], [12, zt - 1], [6, zt - 1]]))], yc - 97, yc - 2, { hinge: true });
      }
      for (let k = n0; k < out.length; k++) hingeParts.push({ door: false, s: out[k] });
    }
    // Door stoppers: a Ø12 pin with a Ø15 collar, 18.5 mm per unit, on the door's
    // free edge against the inside face of the closed glass.
    if (!v.noStoppers) {
      const sx = v.door[0], sz = -(GT + 7.5);
      const unit = (y0: number, down: boolean): void => {
        for (const [a, b, rr] of [[0, 6.8, 6], [6.8, 11.8, 7.5], [11.8, 18.5, 6]]) {
          const ya = down ? y0 - b : y0 + a;
          plan('hardware', frame, [circle(sx, sz, rr, 16)], ya, ya + (b - a));
        }
      };
      if (v.soglia) unit(THRESHOLD_H, false); else { unit(0, false); unit(18.5, false); }
      unit(H - 79, true);
    }
  }

  // The glazing gaskets on both faces of every fixed-pane edge in the frame.
  if (!v.wood && !o.bare) {
    const yMouth = H - 79;
    for (const side of [1, -1]) {
      const gap = side > 0 ? CINGI_GAP.out : CINGI_GAP.in;
      const sec = [ring(SEAL_CINGI[0]).map((q) => ({ x: q.x, y: side * (GT + q.y * (gap - GT)) }))];
      const yb = v.soglia ? THRESHOLD_H : UH;
      for (const f of [fL, fR]) {
        if (!f) continue;
        for (const q of panesOf(f)) {
          if (q[0] < 79 && o.dropJamb !== 'L') put('gasket', frame, sec, (c, e) => ({ x: 79 - c.x, y: e ? yMouth : yb, z: c.y }));
          if (q[1] > W - 79 && o.dropJamb !== 'R') put('gasket', frame, sec, (c, e) => ({ x: W - 79 + c.x, y: e ? yMouth : yb, z: c.y }));
          const a = Math.max(q[0], 79), b = Math.min(q[1], W - 79);
          if (b <= a) continue;
          put('gasket', frame, sec, (c, e) => ({ x: e ? b : a, y: yMouth + c.x, z: c.y }));
          if (v.soglia) put('gasket', frame, sec, (c, e) => ({ x: e ? b : a, y: THRESHOLD_H - c.x, z: c.y }));
        }
      }
    }
  }

  // Turn the door: its leaf (and the leaf's seals and handle) and its half of each hinge.
  if (o.open && !o.noDoor) {
    const th = (o.open * Math.PI) / 180, c = Math.cos(th), s = Math.sin(th), AX = v.axis[0], AZ = v.axis[1];
    const hingeRight = Math.abs(AX - v.door[1]) < Math.abs(AX - v.door[0]);
    const sg = hingeRight ? 1 : -1;
    const turn = (q: P3): P3 => {
      const dx = q.x - AX, dz = q.z - AZ;
      return { x: AX + dx * c + sg * dz * s, y: q.y, z: AZ - sg * dx * s + dz * c };
    };
    const doorSolids = new Set<GlazingSolid>([...out.slice(leafStart).filter((q) => q.part.kind === 'door' && !q.hinge), ...hingeParts.filter((h) => h.door).map((h) => h.s)]);
    for (const q of doorSolids) q.ends = [q.ends[0].map((l) => l.map(turn)), q.ends[1].map((l) => l.map(turn))];
  }
  return out;
}

/** SE10 on the door's hinge edge, cut flush with the door clamps. */
function hingeSealSegs(v: GlazingLayoutV): Span[] {
  const out: Span[] = [];
  let y = v.doorY[0];
  [...v.hingeY].sort((a, b) => a - b).forEach((c) => { if (c + 2 > y) out.push([y, c + 2]); y = c + 87; });
  if (v.doorY[1] > y) out.push([y, v.doorY[1] + SE10_H]);
  return out;
}

// ---- A corner glazing -----------------------------------------------------------

const EXT = 150, EXT_S = 1300;

/** A corner glazing (the configurator's `buildAll`): the front run carried past
 *  the corner and mitred, the return run (a one-pane glazing whose door is left
 *  out) mitred and turned 90° onto it, the joint between their glass. */
function cornerSolids(p: GlazingPlan, open: number): GlazingSolid[] {
  const i = p.input, cc = p.corner!, v = clone(p.v), mirror = p.mirror;
  const wood = v.wood, G = i.glassThickness / 2, zo = Z_OUT[i.mat], W0 = v.W, { sg, E, adj } = cc;
  const Xc = E === 'R' ? W0 - zo : zo;
  if (adj) {
    const off = wood ? G + 12 : 10, target = E === 'R' ? Xc - off : Xc + off, d = target - (E === 'R' ? v.door[1] : v.door[0]), m = (x: number): number => x + d;
    v.door = [m(v.door[0]), m(v.door[1])]; v.handle = [m(v.handle[0]), m(v.handle[1])]; v.axis = [m(v.axis[0]), v.axis[1]];
    const f = E === 'R' ? v.fixed.reduce((a, b) => (b[1] > a[1] ? b : a)) : v.fixed.reduce((a, b) => (b[0] < a[0] ? b : a));
    if (E === 'R') f[1] += d; else f[0] += d;
    v.capX = E === 'R' ? [v.capX[0] + d, W0] : [0, v.capX[1] + d];
  } else {
    const f = E === 'R' ? v.fixed.reduce((a, b) => (b[1] > a[1] ? b : a)) : v.fixed.reduce((a, b) => (b[0] < a[0] ? b : a));
    if (E === 'R') f[1] = Xc + G; else f[0] = Xc - G;
  }
  v.uprof = v.soglia ? [] : v.fixed.map((f): Span => [Math.max(f[0], 79), Math.min(f[1], W0 - 79)]).map(([a, b]): Span => [E === 'L' && a <= 79 ? 0 : a, E === 'R' && b >= W0 - 79 ? W0 : b]);
  // The front run, carried EXT past the corner (shifted for a left corner).
  const vF = clone(v);
  if (adj) vF.noStoppers = true;
  if (E === 'L') {
    const s = (x: number): number => x + EXT, P = (q: Span): Span => [s(q[0]), s(q[1])];
    vF.fixed = vF.fixed.map(P); vF.door = P(vF.door); vF.handle = P(vF.handle); vF.capX = P(vF.capX); vF.uprof = vF.uprof.map(P); vF.hingeSeal = P(vF.hingeSeal); vF.axis = [s(vF.axis[0]), vF.axis[1]];
  }
  vF.W = W0 + EXT;
  vF.uprof = vF.uprof.map(([a, b]): Span => [E === 'L' && a <= EXT ? 0 : a, E === 'R' && b >= W0 ? vF.W : b]);
  if (adj) { vF.capX = E === 'R' ? [vF.capX[0], Xc - G] : [Xc + EXT + G, vF.capX[1]]; vF.capCut = E; }
  const XcF = E === 'R' ? Xc : Xc + EXT;
  // The mitre: the front keeps x ≤ XcF + z (a right corner) — everything but its
  // glass and its door, which stop short of it.
  const front = mitre(buildRun(vF, i, { open, dropJamb: E }), E === 'R' ? 1 : -1, XcF);
  // The return: a one-pane glazing of its own (base `sx` or `dx`), its door beyond the corner.
  const sgL = -sg, keyS = sgL < 0 ? 'sx' : 'dx', wallIn = holeIn(i.mat), Ds = cc.D + wallIn, pocket = wood ? 61 : 62;
  const profile: 'A' | 'B' = !wood && i.config === 4 ? 'B' : 'A';
  const vS = sized(doorSized(BASES[keyS], DOOR_W0, v.H, keyS, profile), Ds + EXT_S);
  Object.assign(vS, { noStoppers: true, wood, soglia: v.soglia, fixedY: v.fixedY.slice(), doorY: v.doorY.slice(), H: v.H });
  let XcS: number;
  if (sgL < 0) {
    XcS = EXT_S + zo; const a = adj ? (wood ? XcS - G : XcS + 10) : XcS + G + 2;
    vS.fixed = [[a, vS.W - pocket]]; vS.capX = adj ? [0, a] : [XcS + G, a]; vS.uprof = v.soglia ? [] : [[0, vS.W - 79]];
  } else {
    XcS = Ds - zo; const b = adj ? (wood ? XcS + G : XcS - 10) : XcS - G - 2;
    vS.fixed = [[pocket, b]]; vS.capX = adj ? [b, vS.W] : [b, XcS - G]; vS.uprof = v.soglia ? [] : [[79, vS.W]];
  }
  const sideRun = mitre(buildRun(vS, i, { open: 0, noDoor: true, dropJamb: sgL < 0 ? 'L' : 'R' }), sgL as 1 | -1, XcS)
    .map((s) => (s.part.kind === 'fixed' ? { ...s, part: { kind: 'side', index: 0 } as GlazingPart } : s));
  // Into the configurator's world (x centred on the front, mirrored): the front as
  // built, the return turned sg·90° about y with its corner plane on the front's.
  const S = mirror ? -1 : 1;
  const toW = (q: P3): P3 => ({ x: S * (q.x - vF.W / 2), y: q.y, z: q.z });
  const cxW = S * (XcF - vF.W / 2), cxL = XcS - vS.W / 2;
  const sideToW = (q: P3): P3 => { const x = q.x - vS.W / 2; return { x: sg * q.z + cxW, y: q.y, z: -sg * x + cxL * sg }; };
  const all: GlazingSolid[] = [
    ...front.map((s) => mapSolid(s, toW, S < 0)),
    ...sideRun.map((s) => mapSolid(s, sideToW, sg < 0)),
  ];
  // The joint where the two runs' glass meets.
  const y0 = adj ? v.doorY[0] : v.fixedY[0], y1 = adj ? v.doorY[1] : v.fixedY[1];
  if (!adj) all.push(boxSolid('silicone', { kind: 'frame' }, cxW - G, cxW + G, v.fixedY[0], v.fixedY[1], -G - 2, -G));
  else if (wood) all.push(boxSolid('magnet', { kind: 'frame' }, cxW - sg * (G + 6), cxW - sg * G, y0, y1, -G, G));
  else {
    // The door's magnetic seal mirrored across the corner's bisector onto the corner glass.
    const yA = v.soglia ? THRESHOLD_H : v.fixedY[0], yB = v.H - 79;
    for (const s of front) {
      if (s.part.kind !== 'door' || (s.stuff !== 'seal' && s.stuff !== 'magnet') || s.sec.length > 2 || !isMag(s)) continue;
      const w = mapSolid(s, toW, S < 0);
      const refl = (q: P3, e: 0 | 1): P3 => ({ x: cxW + sg * q.z, y: e ? yB : yA, z: sg * (q.x - cxW) });
      all.push({ ...w, part: { kind: 'frame' }, ends: [w.ends[0].map((l) => l.map((q) => refl(q, 0))), w.ends[1].map((l) => l.map((q) => refl(q, 1)))] });
    }
  }
  // Back to the glazing's frame: x from the front frame's left outer edge.
  const shiftL = E === 'L' ? EXT : 0;
  const back = (q: P3): P3 => {
    const xv = (S > 0 ? q.x : -q.x) + vF.W / 2 - shiftL;
    return { x: S > 0 ? xv : W0 - xv, y: q.y, z: q.z };
  };
  const out = all.map((s) => mapSolid(s, back, false));
  numberPanes(out);
  return out;
}

const isMag = (s: GlazingSolid): boolean => s.sec[0].length === SEAL_MAG[0].length / 2 || s.sec[0].length === SEAL_MAG_MAGNET[0].length / 2;

function mapSolid(s: GlazingSolid, f: (q: P3) => P3, flip: boolean): GlazingSolid {
  return { ...s, sec: flip ? s.sec.map((l) => l.map((q) => ({ x: -q.x, y: q.y }))) : s.sec, ends: [s.ends[0].map((l) => l.map(f)), s.ends[1].map((l) => l.map(f))] };
}

function boxSolid(stuff: GlazingStuff, part: GlazingPart, x0: number, x1: number, y0: number, y1: number, z0: number, z1: number): GlazingSolid {
  const sec = [rect(Math.min(x0, x1), Math.min(z0, z1), Math.max(x0, x1), Math.max(z0, z1))];
  const at = (y: number): P3[][] => sec.map((l) => l.map((q) => ({ x: q.x, y, z: q.y })));
  return { stuff, part, sec, ends: [at(Math.min(y0, y1)), at(Math.max(y0, y1))] };
}

/** Cut a run's solids at a corner's 45° plane through (xc, z 0): what runs along
 *  x ends on the plane, what lies past it goes; its glass and its door are sized
 *  to stop short of it already. `dir` +1 keeps x ≤ xc + z, −1 keeps x ≥ xc − z. */
function mitre(solids: GlazingSolid[], dir: 1 | -1, xc: number): GlazingSolid[] {
  const past = (q: P3): number => dir * (q.x - xc) - q.z;
  const out: GlazingSolid[] = [];
  for (const s of solids) {
    if (s.stuff === 'glass' || s.part.kind === 'door') { out.push(s); continue; }
    const pts = [...s.ends[0].flat(), ...s.ends[1].flat()];
    if (pts.every((q) => past(q) <= 1e-6)) { out.push(s); continue; }
    if (pts.every((q) => past(q) >= -1e-6)) continue;
    // Along x: the two ends differ only in x — each point's far end comes back to the plane.
    const alongX = s.ends[0].every((l, k) => l.every((q, j) => Math.abs(q.y - s.ends[1][k][j].y) < 1e-6 && Math.abs(q.z - s.ends[1][k][j].z) < 1e-6));
    if (!alongX) continue;
    const onPlane = (q: P3): P3 => ({ ...q, x: xc + dir * q.z });
    const ends = s.ends.map((e) => e.map((l) => l.map((q) => (past(q) > 0 ? onPlane(q) : q)))) as [P3[][], P3[][]];
    out.push({ ...s, ends });
  }
  return out;
}

// ---- Meshes, pieces, weights ----------------------------------------------------

export interface GlazingMesh { positions: number[]; indices: number[] }

/** A solid's triangles (outward facing): its two end caps and its sides. */
export function solidMesh(s: GlazingSolid): GlazingMesh {
  const positions: number[] = [], indices: number[] = [];
  const idx = new Map<Vec2, number>();
  const base: number[][] = [[], []];
  for (const e of [0, 1] as const) {
    s.sec.forEach((l, k) => l.forEach((q, j) => {
      const p = s.ends[e][k][j];
      if (e === 0) idx.set(q, positions.length / 3);
      base[e].push(positions.length / 3);
      positions.push(p.x, p.y, p.z);
    }));
  }
  const n0 = base[0].length;
  // The caps wind as the outline does (a mirrored section runs clockwise), so
  // they agree with the side quads walked in the loops' own order.
  const sense = Math.sign(area2(s.sec[0]));
  const tris = earClip(s.sec[0], s.sec.slice(1));
  for (const t of tris) {
    let a = idx.get(t[0]), b = idx.get(t[1]), c = idx.get(t[2]);
    if (a == null || b == null || c == null) continue;
    if (Math.sign(area2(t)) !== sense) [b, c] = [c, b];
    indices.push(a, c, b, a + n0, b + n0, c + n0);
  }
  let off = 0;
  for (const l of s.sec) {
    for (let j = 0; j < l.length; j++) {
      const a = off + j, b = off + ((j + 1) % l.length);
      indices.push(a, b, b + n0, a, b + n0, a + n0);
    }
    off += l.length;
  }
  // Outward: the signed volume positive (the windings above depend on how the
  // section maps into space).
  if (signedVolume(positions, indices) < 0) for (let k = 0; k < indices.length; k += 3) { const t = indices[k + 1]; indices[k + 1] = indices[k + 2]; indices[k + 2] = t; }
  return { positions, indices };
}

function signedVolume(p: number[], ix: number[]): number {
  let v = 0;
  for (let k = 0; k < ix.length; k += 3) {
    const a = ix[k] * 3, b = ix[k + 1] * 3, c = ix[k + 2] * 3;
    v += p[a] * (p[b + 1] * p[c + 2] - p[b + 2] * p[c + 1]) - p[a + 1] * (p[b] * p[c + 2] - p[b + 2] * p[c]) + p[a + 2] * (p[b] * p[c + 1] - p[b + 1] * p[c]);
  }
  return v / 6;
}

/** A solid's volume (mm³). */
export function solidVolume(s: GlazingSolid): number {
  const m = solidMesh(s);
  return Math.abs(signedVolume(m.positions, m.indices));
}

/** A solid as convex pieces, each its corners at both ends (what a plan cut or
 *  a section slices). */
export function solidPieces(s: GlazingSolid): P3[][] {
  const idx = new Map<Vec2, [number, number]>();
  s.sec.forEach((l, k) => l.forEach((q, j) => idx.set(q, [k, j])));
  const tris = earClip(s.sec[0], s.sec.slice(1));
  const out: P3[][] = [];
  for (const piece of convexMerge(tris)) {
    const corners: P3[] = [];
    for (const e of [0, 1] as const) {
      for (const q of piece) {
        const at = idx.get(q);
        if (at) corners.push(s.ends[e][at[0]][at[1]]);
      }
    }
    if (corners.length >= 6) out.push(corners);
  }
  return out;
}

const DENSITY: Record<GlazingStuff, number> = { profile: 2700e-9, wood: 470e-9, glass: 2500e-9, hardware: 7900e-9, seal: 0, magnet: 0, silicone: 0, gasket: 0 };

/** The handle's weight (kg), as the configurator estimates it: a 1 mm steel
 *  tube (Q: two 980 mm bars, four standoffs, caps; R: two Ø30 × 500 bars). */
export function handleKg(handle: GlazingHandle): number {
  const STEEL = 7900, tube = (30 * 30 - 28 * 28) * 1e-6;
  return handle === 'R'
    ? STEEL * 2 * (Math.PI * (0.015 ** 2 - 0.0135 ** 2) * 0.5 + 2 * Math.PI * 0.0135 ** 2 * 0.06)
    : STEEL * (2 * tube * 0.98 + 4 * tube * 0.045 + 4 * 0.03 * 0.03 * 0.001);
}

/** The leaf's weight (its glass and handle) and the whole glazing's (glass,
 *  profiles, hinges at 2 kg each), kg — the configurator's: the handle and the
 *  hinges are estimates, the rest the solids' volumes. */
export function glazingWeights(p: GlazingPlan, solids: readonly GlazingSolid[] = glazingSolids(p)): { leaf: number; total: number } {
  let leaf = handleKg(glazingOptions(p.input).handle), total = 0;
  for (const s of solids) {
    if (s.hinge || (s.leaf && s.stuff === 'hardware')) continue;
    const kg = DENSITY[s.stuff] ? solidVolume(s) * DENSITY[s.stuff] : 0;
    if (s.leaf) leaf += kg;
    else if (s.part.kind !== 'door') total += kg;
  }
  return { leaf, total: leaf + total + 2 * p.v.hingeY.length };
}

/** The clear passage through the open door (mm), as the configurator measures
 *  it: with the door at 90° (`open90`, the glazing's solids so), the nearest
 *  solid either side of the door's middle (the handle aside) and the lowest one
 *  over it; `band`, how deep the head is. */
export function netPassage(p: GlazingPlan, open90: readonly GlazingSolid[] = glazingSolids({ ...p, corner: null }, 90), band = FRAME_BAND): { w: number; h: number } {
  const solids = open90.filter((s) => !(s.leaf && s.stuff === 'hardware'));
  const v = p.v, yTop = v.H - band;
  const c = p.mirror ? v.W - (v.door[0] + v.door[1]) / 2 : (v.door[0] + v.door[1]) / 2;
  const boxes = solids.map((s) => {
    let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
    for (const e of s.ends) for (const l of e) for (const q of l) { x0 = Math.min(x0, q.x); x1 = Math.max(x1, q.x); y0 = Math.min(y0, q.y); y1 = Math.max(y1, q.y); }
    return { x0, x1, y0, y1 };
  });
  let L = -Infinity, R = Infinity;
  for (const b of boxes) {
    if (b.y0 > yTop - 2) continue;
    if (b.x1 <= c) L = Math.max(L, b.x1); else if (b.x0 >= c) R = Math.min(R, b.x0);
  }
  let top = Infinity, bot = 0;
  for (const b of boxes) {
    if (b.x1 <= L + 1 || b.x0 >= R - 1) continue;
    if (b.y0 > 20) top = Math.min(top, b.y0); else if (b.y1 < 50) bot = Math.max(bot, b.y1);
  }
  return { w: R - L, h: top - bot };
}

/** A wood member's UVs in mm, its grain (the image's v) along the member's
 *  length on every face that runs that way, across the face's longer side on
 *  its ends — read in the glazing's own frame, per triangle of the flat mesh. */
export function grainUVs(positions: ArrayLike<number>, indices: ArrayLike<number>): number[] {
  const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
  for (let k = 0; k < positions.length; k++) { lo[k % 3] = Math.min(lo[k % 3], positions[k]); hi[k % 3] = Math.max(hi[k % 3], positions[k]); }
  const span = [0, 1, 2].map((a) => hi[a] - lo[a]);
  const long = span.indexOf(Math.max(...span));
  const uv: number[] = [];
  const at = (i: number, a: number): number => positions[i * 3 + a];
  for (let t = 0; t + 2 < indices.length; t += 3) {
    const [i, j, k] = [indices[t], indices[t + 1], indices[t + 2]];
    const e1 = [0, 1, 2].map((a) => at(j, a) - at(i, a)), e2 = [0, 1, 2].map((a) => at(k, a) - at(i, a));
    const n = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]].map(Math.abs);
    const facing = n.indexOf(Math.max(...n));
    const [a, b] = [0, 1, 2].filter((x) => x !== facing);
    const v = a === long || (b !== long && span[a] >= span[b]) ? a : b, u = v === a ? b : a;
    for (const q of [i, j, k]) uv.push(at(q, u), at(q, v));
  }
  return uv;
}
